import React, { useState, useEffect } from 'react';
import { getResponses } from '../utils/storage';
import { UserResponse, TestVariant, Question } from '../types';

interface AdminDashboardProps {
  variants: TestVariant[];
  questions: Question[];
}

export default function AdminDashboard({ variants, questions }: AdminDashboardProps) {
  const [responses, setResponses] = useState<UserResponse[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<string>('all');

  useEffect(() => {
    // Load responses from storage
    const loadedResponses = getResponses();
    setResponses(loadedResponses);

    // Set up interval to refresh data every 5 seconds
    const intervalId = setInterval(() => {
      setResponses(getResponses());
    }, 5000);

    return () => clearInterval(intervalId);
  }, []);

  const filteredResponses = selectedVariant === 'all'
    ? responses
    : responses.filter(response => response.variantId === selectedVariant);

  const getVariantName = (variantId: string) => {
    const variant = variants.find(v => v.id === variantId);
    return variant ? variant.name : 'Unknown Variant';
  };

  const getQuestionText = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    return question ? question.text : 'Unknown Question';
  };

  const exportToCSV = () => {
    // Create CSV header
    const header = ['User ID', 'Variant', 'Timestamp', ...questions.map(q => q.text)];
    
    // Create CSV rows
    const rows = responses.map(response => {
      const row = [
        response.userId,
        getVariantName(response.variantId),
        new Date(response.timestamp).toISOString()
      ];
      
      // Add answers in the order of questions
      questions.forEach(question => {
        const answer = response.answers.find(a => a.questionId === question.id);
        row.push(answer ? String(answer.answer) : '');
      });
      
      return row;
    });
    
    // Combine header and rows
    const csvContent = [
      header.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Create and download the CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `test-responses-${new Date().toISOString().split('T')[0]}.csv`);
    link.click();
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800">Test Results Dashboard</h2>
        <div className="flex space-x-4">
          <select
            value={selectedVariant}
            onChange={(e) => setSelectedVariant(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Variants</option>
            {variants.map(variant => (
              <option key={variant.id} value={variant.id}>
                {variant.name}
              </option>
            ))}
          </select>
          
          <button
            onClick={exportToCSV}
            className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="mb-6">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Total Responses</p>
            <p className="text-2xl font-bold">{responses.length}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Unique Users</p>
            <p className="text-2xl font-bold">
              {new Set(responses.map(r => r.userId)).size}
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {variants.map(variant => {
            const variantResponses = responses.filter(r => r.variantId === variant.id);
            return (
              <div key={variant.id} className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">{variant.name}</p>
                <p className="text-2xl font-bold">{variantResponses.length}</p>
                <p className="text-xs text-gray-400">
                  {Math.round((variantResponses.length / responses.length) * 100) || 0}% of total
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {filteredResponses.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Variant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                {questions.map(question => (
                  <th key={question.id} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {question.text.substring(0, 20)}...
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredResponses.map((response, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {response.userId.substring(0, 8)}...
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {getVariantName(response.variantId)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(response.timestamp).toLocaleString()}
                  </td>
                  {questions.map(question => {
                    const answer = response.answers.find(a => a.questionId === question.id);
                    return (
                      <td key={question.id} className="px-6 py-4 text-sm text-gray-500">
                        {answer ? String(answer.answer) : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500">No responses recorded yet.</p>
        </div>
      )}
    </div>
  );
}